/**
 * List uploaded packing list files (parity with ERP).
 * GET /api/files?kind=packing_list; select file_id to copy or send to Orion.
 */
import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';

export default function PackingListFilesModal({ open, onClose, onSelectFileId, t, locale }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isAr = locale === 'ar';

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get('/api/files', { params: { kind: 'packing_list', limit: 50 }, timeout: 10000 });
      setList(res.data?.files ?? []);
    } catch (err) {
      setError(err?.response?.data?.error ?? (isAr ? 'فشل تحميل القائمة' : 'Failed to load list'));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const copyAndUse = (fileId) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(fileId).catch(() => {});
    }
    onSelectFileId?.(fileId);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div className="console-history-backdrop" onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()} role="button" tabIndex={0} aria-label={isAr ? 'إغلاق' : 'Close'} />
      <aside className="console-settings-sidebar console-packlist-modal ui-modal ui-modal--md" aria-label={isAr ? 'ملفات قائمة التعبئة' : 'Packing list files'}>
        <div className="console-settings-header ui-modal__header">
          <h2 className="console-settings-title">{isAr ? 'ملفات قائمة التعبئة المرفوعة' : 'Uploaded packing list files'}</h2>
          <button type="button" className="console-btn console-btn-close" onClick={onClose} aria-label={t('closeHelp')}>×</button>
        </div>
        <div className="console-settings-body ui-modal__body">
          {loading && <p className="console-settings-loading">{t('loading')}</p>}
          {error && <p className="console-settings-error" role="alert">{error}</p>}
          {!loading && list.length === 0 && !error && (
            <p className="console-history-empty">{isAr ? 'لا توجد ملفات مرفوعة.' : 'No uploaded files.'}</p>
          )}
          {!loading && list.length > 0 && (
            <ul className="console-packlist-list">
              {list.map((f) => (
                <li key={f.file_id} className="console-packlist-item">
                  <span className="console-packlist-name" title={f.name}>{f.name}</span>
                  <span className="console-packlist-meta">
                    {f.created_at ? String(f.created_at).slice(0, 10) : '—'}
                    {f.size != null && ` · ${Math.round(f.size / 1024)} KB`}
                  </span>
                  <button
                    type="button"
                    className="console-btn console-packlist-use"
                    onClick={() => copyAndUse(f.file_id)}
                  >
                    {isAr ? 'نسخ واستخدام' : 'Copy & use'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}
